import { api } from './api';

export interface Product {
  id: string;
  sku: string;
  name: string;
  description?: string;
  categoryId?: string;
  category?: {
    id: string;
    name: string;
  };
  itemType: 'SERVICE' | 'INVENTORY' | 'SERIALIZED';
  unitPrice: number;
  costPrice: number;
  taxCodeId?: string;
  taxCode?: {
    id: string;
    name: string;
    rate: number;
  };
  isActive: boolean;
  isSerialized: boolean;
  trackInventory: boolean;
  reorderPoint?: number;
  reorderQty?: number;
  preferredVendorId?: string;
  preferredVendor?: {
    id: string;
    name: string;
  };
  leadTimeDays?: number;
  cogsAccountId?: string;
  cogsAccount?: { id: string; code: string; name: string };
  assetAccountId?: string;
  assetAccount?: { id: string; code: string; name: string };
  incomeAccountId?: string;
  incomeAccount?: { id: string; code: string; name: string };
  barcode?: string;
  imageUrl?: string;
  weight?: number;
  dimensions?: string;
  totalStock?: number;
  availableStock?: number;
  totalReserved?: number;
  rentedCount?: number;
  isLowStock?: boolean;
  warehouseStock?: Array<{
    warehouse: {
      id: string;
      code: string;
      name: string;
    };
    quantityOnHand: number;
    quantityReserved: number;
    binLocation?: string;
  }>;
  stockMovements?: Array<{
    id: string;
    movementType: string;
    quantity: number;
    date: string;
    warehouse: {
      code: string;
      name: string;
    };
  }>;
  serialNumbers?: Array<{
    id: string;
    serialNumber: string;
    status: string;
    warehouse?: {
      code: string;
      name: string;
    };
  }>;
  createdAt: string;
  updatedAt: string;
}

export interface ProductFilterParams {
  page?: number;
  limit?: number;
  search?: string;
  categoryId?: string;
  itemType?: 'SERVICE' | 'INVENTORY' | 'SERIALIZED';
  isActive?: boolean;
  lowStock?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface CreateProductDto {
  sku?: string;
  name: string;
  description?: string;
  categoryId?: string;
  itemType: 'SERVICE' | 'INVENTORY' | 'SERIALIZED';
  unitPrice: number;
  costPrice: number;
  taxCodeId?: string;
  isActive?: boolean;
  isSerialized?: boolean;
  trackInventory?: boolean;
  reorderPoint?: number;
  reorderQty?: number;
  preferredVendorId?: string;
  leadTimeDays?: number;
  // null explicitly clears the override (falls back to category/company default); undefined leaves it unchanged.
  cogsAccountId?: string | null;
  assetAccountId?: string | null;
  incomeAccountId?: string | null;
  barcode?: string;
  imageUrl?: string;
  weight?: number;
  dimensions?: string;
}

export interface ProductStats {
  totalProducts: number;
  activeProducts: number;
  inactiveProducts: number;
  serviceItems: number;
  inventoryItems: number;
  serializedItems: number;
  lowStockItems: number;
}

const productService = {
  // Get all products with filters
  getProducts: async (params?: ProductFilterParams) => {
    const response = await api.get('/products', { params });
    return response.data;
  },

  // Get single product by ID
  getProduct: async (id: string) => {
    const response = await api.get(`/products/${id}`);
    return response.data;
  },

  // Create new product
  createProduct: async (data: CreateProductDto) => {
    const response = await api.post('/products', data);
    return response.data;
  },

  // Update product
  updateProduct: async (id: string, data: Partial<CreateProductDto>) => {
    const response = await api.put(`/products/${id}`, data);
    return response.data;
  },

  // Delete product (soft delete)
  deleteProduct: async (id: string) => {
    const response = await api.delete(`/products/${id}`);
    return response.data;
  },

  // Get product statistics
  getProductStats: async (): Promise<ProductStats> => {
    const response = await api.get('/products/stats');
    return response.data.data;
  },
};

export default productService;
